import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DialogTabs } from '@/components/ui/DialogTabs'
import { AuditLog } from '@/features/audit/AuditLog'
import { Dialog } from '@/components/ui/Dialog'
import {
  useMe,
  useSetUserDisabled,
  useUsers,
  instanceLimitsQueryOptions,
  instanceQueryOptions,
  useInstance,
  useInstanceLimits,
  useSetInstanceLimits,
  useSetRegistrationPolicy,
} from '@/features/auth/api'
import type { InstanceLimitsInput, RegistrationPolicy } from '@/gql/graphql'
import { errorMessage } from '@/lib/graphql'

import './settings.css'

const POLICIES: { value: RegistrationPolicy; name: string; detail: string }[] = [
  {
    value: 'OPEN',
    name: 'Open',
    detail: 'Anyone who finds this instance can create an account.',
  },
  {
    value: 'INVITE_ONLY',
    name: 'Invite only',
    detail:
      'New accounts need an invite link. Following one registers and joins that lounge in a single step. Recommended for anything on the public internet.',
  },
  {
    value: 'CLOSED',
    name: 'Closed',
    detail: 'No new accounts at all. Existing ones keep working.',
  },
]

export const Route = createFileRoute('/_app/settings/instance')({
  beforeLoad: ({ context }) => {
    // The server refuses the mutation regardless — this only keeps the door
    // from being visible to someone it would slam on.
    if (!context.me?.isInstanceAdmin) throw redirect({ to: '/' })
  },
  loader: ({ context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(instanceQueryOptions()),
      // Loaded with the modal rather than when the Limits tab is first clicked,
      // so switching tabs never shows a spinner. It is admin-only, and so is
      // this route.
      queryClient.ensureQueryData(instanceLimitsQueryOptions()),
    ]),
  component: InstanceSettings,
})

type Tab = 'general' | 'limits' | 'people' | 'audit'

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'limits', label: 'Limits' },
  { id: 'people', label: 'People' },
  { id: 'audit', label: 'Audit log' },
] as const

/**
 * One tunable number, as the form needs it.
 *
 * `min` and `max` mirror the server's ranges, and that duplication is
 * deliberate: the attribute is a courtesy that stops a typo before it travels,
 * and the server's refusal — shown as written, below the group — is the cap. The
 * same doctrine as the video publisher limit's client-side pre-check.
 */
interface LimitField {
  key: keyof InstanceLimitsInput
  label: string
  min: number
  max: number
  hint: string
  /**
   * The thing this number is *about*, where several numbers describe one thing.
   *
   * Camera height, frame rate and bitrate are three halves of one decision, so
   * they carry `cluster: 'Camera'` and are laid out as a titled row rather than as
   * three peers in a list. Consecutive fields sharing a cluster are rendered
   * together; a field without one stands alone. Deriving the layout from the data
   * rather than nesting the data means `LimitsTab` still walks a flat list, and
   * adding a limit is still one entry.
   */
  cluster?: string
}

interface LimitGroup {
  title: string
  blurb: string
  fields: LimitField[]
  /**
   * Give this group the full width of the grid rather than one column of it.
   *
   * For a group whose fields are themselves laid out side by side — without the
   * width, a titled row of three degrades to a stack of three and the grouping
   * buys nothing.
   */
  wide?: boolean
}

const LIMIT_GROUPS: LimitGroup[] = [
  {
    title: 'Uploads',
    blurb: 'What lands on your disk, and how big it may be.',
    fields: [
      {
        key: 'attachmentMaxMb',
        cluster: 'Attachments',
        label: 'Largest, MB',
        min: 1,
        max: 1000,
        hint: 'Counted as the bytes arrive, so nothing oversized is written. 25 is the default; the ceiling is room to opt in, not advice.',
      },
      {
        key: 'attachmentMaxPerMessage',
        cluster: 'Attachments',
        label: 'Per message',
        min: 1,
        max: 20,
        hint: 'More than a handful stops reading as a message and starts reading as a folder.',
      },
      {
        key: 'avatarMaxMb',
        label: 'Avatar, MB',
        min: 1,
        max: 32,
        hint: 'Lower than the file cap on purpose: an avatar is decoded whole in memory, where an attachment is only streamed to disk.',
      },
      {
        key: 'uploadPerHour',
        label: 'Uploads / hour',
        min: 1,
        max: 1000,
        hint: 'Generous for a person, ruinous for a script. Avatars and attachments share it.',
      },
    ],
  },
  {
    title: 'Messages',
    blurb: 'Two windows, so a burst of typing is fine and a loop is not.',
    fields: [
      {
        key: 'messageBurstPerSecond',
        label: 'Per second',
        min: 1,
        max: 20,
        hint: 'What fast typing and a pasted paragraph actually look like.',
      },
      {
        key: 'messagePerMinute',
        label: 'Per minute',
        min: 5,
        max: 600,
        hint: 'The longer window, so nothing can sit at the burst ceiling forever.',
      },
    ],
  },
  {
    title: 'Invites and sign-up',
    blurb: 'How fast an instance can grow, and how hard a password can be guessed.',
    fields: [
      {
        key: 'invitesPerUserHourly',
        cluster: 'Invites',
        label: 'Per person',
        min: 1,
        max: 200,
        hint: 'Hourly, counted from the invites themselves — revoking one frees the slot at once.',
      },
      {
        key: 'liveInvitesPerLounge',
        cluster: 'Invites',
        label: 'Live per lounge',
        min: 1,
        max: 500,
        hint: 'Every unexpired code is a door. A list too long to read is a list nobody prunes.',
      },
      {
        key: 'registerPerIpHourly',
        cluster: 'Sign-ups per address',
        label: 'Hourly',
        min: 1,
        max: 100,
        hint: 'The first account on a fresh instance is always allowed through, whatever this says.',
      },
      {
        key: 'registerPerIpDaily',
        cluster: 'Sign-ups per address',
        label: 'Daily',
        min: 1,
        max: 1000,
        hint: 'The slower ceiling behind the hourly one.',
      },
      {
        key: 'loginPerIp',
        cluster: 'Sign-in',
        label: 'Attempts',
        min: 5,
        max: 200,
        hint: 'Per fifteen minutes, whichever account they name. One address can be a whole household.',
      },
      {
        key: 'loginFailuresBeforeLock',
        cluster: 'Sign-in',
        label: 'Lock after',
        min: 3,
        max: 20,
        hint: 'Failures, then thirty seconds doubling to a quarter of an hour. A speed bump, not a ban.',
      },
    ],
  },
  {
    title: 'Voice and video',
    blurb:
      'Two numbers rather than one, because a screen share costs 1–3 Mbps against a voice stream’s 40–60 kbps.',
    fields: [
      {
        key: 'voiceParticipantLimit',
        cluster: 'Per nook',
        label: 'In voice',
        min: 1,
        max: 64,
        hint: 'A bandwidth guard, not a technical limit. Lowering it never removes anybody from a call already happening.',
      },
      {
        key: 'videoPublisherLimit',
        cluster: 'Per nook',
        label: 'Publishing video',
        min: 0,
        max: 16,
        hint: 'People, not tracks: somebody running a camera and a screen takes one slot. Set it to zero to make this a voice-only instance. Lowering it never stops a camera that is already running.',
      },
    ],
  },
  {
    title: 'Picture and sound quality',
    wide: true,
    blurb:
      'Ceilings, not targets — the encoder spends what the picture needs, so a talking head uses 2000–4000 kbps of a 9000 allowance and only fast movement uses the lot. Each person may choose less on their own device; nobody may choose more. The defaults assume a well-connected host: six publishers at the full allowance is around 270 Mbps of upload. Lower the bitrates first if that is not you.',
    fields: [
      {
        key: 'cameraMaxHeight',
        cluster: 'Camera',
        label: 'Resolution',
        min: 360,
        max: 1080,
        hint: 'Pixels tall. Most webcams stop at 1080.',
      },
      {
        key: 'cameraMaxFps',
        cluster: 'Camera',
        label: 'Frame rate',
        min: 15,
        max: 60,
        hint: 'An upper bound, not a demand — many webcams manage 30 regardless.',
      },
      {
        key: 'cameraMaxKbps',
        cluster: 'Camera',
        label: 'Bitrate',
        min: 200,
        max: 20000,
        hint: 'kbps per person sending. The first number to lower if your upload is the constraint.',
      },
      {
        key: 'screenMaxHeight',
        cluster: 'Screen share',
        label: 'Resolution',
        min: 720,
        max: 1440,
        hint: 'Pixels tall. A shared window smaller than this is never stretched to fill it.',
      },
      {
        key: 'screenMaxFps',
        cluster: 'Screen share',
        label: 'Frame rate',
        min: 5,
        max: 60,
        hint: 'The one place frames matter more than bitrate: 15 is a slide deck, 60 is a game.',
      },
      {
        key: 'screenMaxKbps',
        cluster: 'Screen share',
        label: 'Bitrate',
        min: 300,
        max: 20000,
        hint: 'kbps. A text editor sends almost none of it; a game sends all of it.',
      },
      {
        key: 'voiceAudioKbps',
        cluster: 'Voice',
        label: 'Bitrate',
        min: 12,
        max: 128,
        hint: 'kbps per person talking — nothing beside one camera, and voice is what people came for.',
      },
    ],
  },
]

/**
 * Consecutive fields that describe the same thing, so the layout can group them.
 *
 * Carries its own React `key` because every run is non-empty by construction and
 * the type system cannot see that — taking it from `fields[0]` at the call site
 * needs a null check for a case that cannot happen.
 */
function clustersOf(
  fields: LimitField[],
): { key: string; cluster?: string; fields: LimitField[] }[] {
  const runs: { key: string; cluster?: string; fields: LimitField[] }[] = []
  for (const field of fields) {
    const last = runs.at(-1)
    if (last && last.cluster === field.cluster) last.fields.push(field)
    else runs.push({ key: field.key, cluster: field.cluster, fields: [field] })
  }
  return runs
}

function InstanceSettings() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('general')
  const close = () => void navigate({ to: '/' })

  return (
    <Dialog
      open
      onClose={close}
      title="Instance settings"
      description="You are the admin of this instance."
      /* The widest surface in the app, and the one with most to hold: twenty-one
       * limits, a searchable list of every account, and an audit log whose rows are
       * sentences. `.dialog-wide` is `min(52rem, …)`, so a narrow window still gets
       * the viewport rather than a fixed width. */
      wide
    >
      <DialogTabs<Tab> tabs={TABS} active={tab} onChange={setTab}>
        {tab === 'general' && <GeneralTab />}
        {tab === 'limits' && <LimitsTab />}
        {tab === 'people' && <PeopleTab />}
        {/* `null` is the instance-scoped log: policy changes, caps, accounts
         * disabled. Those belong to no lounge, and only this door opens them. */}
        {tab === 'audit' && <AuditLog loungeId={null} />}
      </DialogTabs>

      <div className="dialog-actions">
        <Button onClick={close}>Close</Button>
      </div>
    </Dialog>
  )
}

function GeneralTab() {
  const { data } = useInstance()
  const setPolicy = useSetRegistrationPolicy()
  const current = data?.instance.registrationPolicy

  return (
    <>
      <div className="dialog-body">
        <div>
          <p className="field-label">Who can register</p>
          <div className="settings-options" role="radiogroup" aria-label="Registration policy">
            {POLICIES.map((policy) => (
              <button
                key={policy.value}
                type="button"
                role="radio"
                aria-checked={current === policy.value}
                data-selected={current === policy.value}
                className="settings-option"
                disabled={setPolicy.isPending}
                onClick={() => setPolicy.mutate(policy.value)}
              >
                <div>
                  <p className="settings-option-name">{policy.name}</p>
                  <p className="settings-option-detail">{policy.detail}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {setPolicy.isError && (
          <p className="form-error" role="alert">
            {errorMessage(setPolicy.error, 'That setting could not be saved.')}
          </p>
        )}

      </div>
    </>
  )
}

/**
 * The numbers that protect the machine this is running on.
 *
 * Save-on-blur, like the settings that used to live in General: twenty-one fields
 * behind one Save button is a form somebody abandons half-filled, and each of
 * these is independent of the others.
 *
 * The refusal appears under the field that caused it, which is why the last
 * field touched is tracked: the server's sentence names the setting, but five
 * copies of it — one per group — would be worse than one in the wrong place.
 */
function LimitsTab() {
  const { data, isPending, isError } = useInstanceLimits()
  const save = useSetInstanceLimits()
  const [lastTouched, setLastTouched] = useState<keyof InstanceLimitsInput | null>(null)

  if (isPending) return <p className="audit-note muted">Loading…</p>
  if (isError || !data) return <p className="audit-note muted">Those settings could not be read.</p>

  const limits = data.instanceLimits

  /* One number, with its own label, hint and refusal. `.field` is the house
   * wrapper — a flex column with a gap, the same one `TextField` uses. Without it a
   * label is inline and a number input is a narrow inline-block, so the two sit
   * side by side: the shape the two limits in General had before they moved here,
   * which is how that bug arrived rather than something new. */
  const numberField = (field: LimitField) => (
    <div className="field" key={field.key}>
      <label className="field-caps" htmlFor={field.key}>
        {field.label}
      </label>
      <input
        id={field.key}
        className="field-input settings-number"
        type="number"
        min={field.min}
        max={field.max}
        /* Uncontrolled, keyed on nothing: a rejected value stays on screen beside
         * the sentence explaining it, which is what lets somebody correct a typo
         * rather than retype the whole field. */
        defaultValue={limits[field.key]}
        disabled={save.isPending}
        onBlur={(event) => {
          const next = Number(event.target.value)
          if (!Number.isInteger(next) || next === limits[field.key]) return
          setLastTouched(field.key)
          save.mutate({ [field.key]: next })
        }}
      />
      <p className="field-hint">{field.hint}</p>

      {save.isError && lastTouched === field.key && (
        <p className="form-error" role="alert">
          {errorMessage(save.error, 'That limit could not be saved.')}
        </p>
      )}
    </div>
  )

  /* A grid rather than `.dialog-body`'s single column, for the reason the profile
   * dialog uses one: twenty-one numbers stacked one per line is a scroll, and the
   * dialog is already wide enough to hold two groups abreast. `.settings-grid`
   * reflows without a breakpoint, so a phone gets the same layout narrower. */
  return (
    <div className="settings-grid">
      {LIMIT_GROUPS.map((group) => (
        <section
          key={group.title}
          className={`settings-section settings-fields${group.wide ? ' settings-span' : ''}`}
        >
          <h3 className="settings-heading">{group.title}</h3>
          <p className="field-hint">{group.blurb}</p>

          {clustersOf(group.fields).map(({ key, cluster, fields }) =>
            cluster ? (
              <div className="field-cluster" key={key}>
                <p className="field-label">{cluster}</p>
                <div className="field-row">{fields.map(numberField)}</div>
              </div>
            ) : (
              <div className="field-row" key={key}>
                {fields.map(numberField)}
              </div>
            ),
          )}
        </section>
      ))}
    </div>
  )
}

/**
 * Every account, and the one thing an admin can do to one.
 *
 * Offset paging rather than a cursor, unlike the log beside it: this list is
 * sorted by handle and searched, so "the next fifty alphabetically" is the
 * question, and there is no meaningful cursor to hand back.
 */
function PeopleTab() {
  const [query, setQuery] = useState('')
  const { data: me } = useMe()
  const { data, isPending } = useUsers(query)
  const setDisabled = useSetUserDisabled(query)
  const [pending, setPending] = useState<{ id: string; name: string } | null>(null)

  const users = data?.users ?? []

  return (
    <>
      <div className="dialog-body">
        <input
          className="field-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by handle or name"
          aria-label="Search people"
        />

        {isPending && <p className="audit-note muted">Loading…</p>}

        {!isPending && users.length === 0 && (
          <p className="audit-note muted">Nobody matches that.</p>
        )}

        <ul className="people-list">
          {users.map((user) => (
            <li key={user.id} className="person" data-disabled={user.disabledAt ? true : undefined}>
              <Avatar displayName={user.displayName} avatarUrl={user.avatarUrl} size={2} />
              <div className="person-who">
                <p className="person-name">{user.displayName}</p>
                <p className="person-handle">
                  {user.disabledAt ? `@${user.handle} — disabled` : `@${user.handle}`}
                </p>
              </div>

              {/* Never yourself: disabling your own account locks the instance
               * out of its own admin panel, and nothing in the product undoes
               * it. The server refuses this too. */}
              {user.id !== me?.me?.id && (
                <Button
                  variant="quiet"
                  disabled={setDisabled.isPending}
                  onClick={() =>
                    user.disabledAt
                      ? setDisabled.mutate({ userId: user.id, disabled: false })
                      : setPending({ id: user.id, name: user.displayName })
                  }
                >
                  {user.disabledAt ? 'Enable' : 'Disable'}
                </Button>
              )}
            </li>
          ))}
        </ul>

        {setDisabled.isError && (
          <p className="form-error" role="alert">
            {errorMessage(setDisabled.error, 'That account could not be changed.')}
          </p>
        )}
      </div>

      {/* Destructive, so it gets the confirm tier. Enabling does not — putting
       * somebody back is not something to be careful about. */}
      <ConfirmDialog
        open={pending !== null}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return
          setDisabled.mutate({ userId: pending.id, disabled: true }, { onSuccess: () => setPending(null) })
        }}
        pending={setDisabled.isPending}
        title={`Disable ${pending?.name}?`}
        description="They are signed out everywhere and cannot sign in again until you enable them. Their messages stay."
        confirmLabel="Disable"
      />
    </>
  )
}
